/**
 * sessionStorage persistence for the performance pages' filter fields, shared by the
 * history (FleetPerformanceHistory/PerformanceHistory) and day/dataset/live performance
 * pages under one key.
 *
 * Contract: Year/Event/Config are HISTORY-PAGE-ONLY fields — the day/dataset/live pages
 * render no inputs for them, so they must never read them from this key (an inherited
 * value would filter their data invisibly and unclearably) and must write via
 * mergeGradeFiltersIntoSession so they never destroy the history pages' saved values.
 *
 * filterState is history-page-only for the same reason. The history pages REQUIRE a single
 * foiling state (they force H0 when empty) so their query always narrows; the day/dataset/live
 * pages default to ALL. Sharing the field let history's forced H0 become those pages' load-time
 * state, which — with STATE now applied in SQL — silently hid every H1/H2 row. Only Grade is
 * shared across all performance pages.
 */
export const PERFORMANCE_FILTERS_SESSION_KEY = 'performanceFilters';

export interface PerformanceSessionFilters {
  filterGrades: string;
  filterYear: string;
  filterEvent: string;
  filterConfig: string;
  filterState: string;
}

/** Load the saved filters (null when nothing saved or the value is corrupt). */
export function loadPerformanceFiltersFromSession(): PerformanceSessionFilters | null {
  try {
    const saved = sessionStorage.getItem(PERFORMANCE_FILTERS_SESSION_KEY);
    if (!saved) return null;
    const filterData = JSON.parse(saved);
    const str = (v: unknown): string => (typeof v === 'string' ? v : '');
    return {
      filterGrades: str(filterData.filterGrades),
      filterYear: str(filterData.filterYear),
      filterEvent: str(filterData.filterEvent),
      filterConfig: str(filterData.filterConfig),
      filterState: str(filterData.filterState),
    };
  } catch {
    return null;
  }
}

/** Full overwrite of all five fields — history-mode writes only. */
export function saveAllPerformanceFiltersToSession(filters: PerformanceSessionFilters): void {
  try {
    sessionStorage.setItem(PERFORMANCE_FILTERS_SESSION_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Write grades while preserving the saved history-only Year/Event/Config/State fields —
 * the only write the day/dataset/live pages may perform.
 */
export function mergeGradeFiltersIntoSession(filterGrades: string): void {
  const existing = loadPerformanceFiltersFromSession();
  saveAllPerformanceFiltersToSession({
    filterYear: existing?.filterYear ?? '',
    filterEvent: existing?.filterEvent ?? '',
    filterConfig: existing?.filterConfig ?? '',
    filterState: existing?.filterState ?? '',
    filterGrades,
  });
}

/** Remove the key entirely — history-mode Clear All only. */
export function clearPerformanceFiltersFromSession(): void {
  try {
    sessionStorage.removeItem(PERFORMANCE_FILTERS_SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
}
